import { useState } from "react";

import type { AttributeReviewMappingRow } from "@/hooks/useAttributeReview";

type Args = {
  row: AttributeReviewMappingRow;
  otherBusy: boolean;
  readOnly: boolean;
  canApprove: boolean;
  afterSettleId: string | null;
  onOpenRow: (id: string | null) => void;
  approveRows: (
    rows: AttributeReviewMappingRow[],
  ) => Promise<{ ok: boolean; error: string | null }>;
  ignoreRows: (rows: AttributeReviewMappingRow[]) => Promise<boolean>;
};

export function useReviewDrawerSave({
  row,
  otherBusy,
  readOnly,
  canApprove,
  afterSettleId,
  onOpenRow,
  approveRows,
  ignoreRows,
}: Args) {
  const [busy, setBusy] = useState<"approve" | "ignore" | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirmIgnore, setConfirmIgnore] = useState(false);
  const locked = busy !== null || otherBusy;

  const approve = async () => {
    if (!canApprove || readOnly || locked) return;
    setBusy("approve");
    setSaveError(null);
    try {
      const result = await approveRows([row]);
      if (result.ok) onOpenRow(afterSettleId);
      else setSaveError(result.error ?? "Save failed. Please try again.");
    } finally {
      setBusy(null);
    }
  };
  const ignore = async () => {
    setConfirmIgnore(false);
    if (locked) return;
    setBusy("ignore");
    try {
      if (await ignoreRows([row])) onOpenRow(afterSettleId);
    } finally {
      setBusy(null);
    }
  };
  const settle = async (action: () => Promise<boolean>) => {
    if (locked) return;
    if (await action()) onOpenRow(afterSettleId);
  };
  return {
    busy,
    saveError,
    confirmIgnore,
    setConfirmIgnore,
    locked,
    approve,
    ignore,
    settle,
  };
}
